import { Inject, Injectable } from '@angular/core';
import { TeethUnitType } from '@shared/models/rx-rules-json-interface';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { IdName } from '@shared/models/id-name';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { AbutmentType } from '@shared/models/abutment-type';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { map, take } from 'rxjs/operators';
import { OrderQuery } from '@modules/order/state/order-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { TranslateService } from '@ngx-translate/core';
import { RxIndicationRulesService } from '@shared/services/rx-rules-helper/rx-indication-rules.service';
import { RxGeneralRules } from '@shared/models/rx-general-rules';
import {
	DENTURE_DETAILS_RULES,
	PROCEDURE_BRIDGE_AVAILABILITY_RULES,
	RESTORATION_CONFIGURATION,
	RX_GENERAL_RULES
} from '@shared/rules/rules-tokens';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { CaseTypeRulesParser } from '@shared/services/rx-rules-helper/case-type-rules.parser';
import { ProcedureToothEditorParser } from '@shared/services/rx-rules-helper/procedure-tooth-editor-parser';
import { CASETYPE_RULES_PARSER, PROCEDURE_TOOTH_EDITOR_PARSER } from '@shared/services/rx-rules-helper/parser-tokens';
import { RxDentureDetailsRules } from '@shared/models/denture-details-rules';
import { TypeEnum } from '@modules/order/models/type.enum';
import { RestorationConfiguration } from '@shared/models/restoration-configuration-interface';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ToothEditorQuery } from '@modules/tooth-editor/state/tooth-editor-query';
import { ProcedureMapCheckedValue, ProcedureMapCompositePattern } from '@core/procedure-helpers/models/procedure-map-composites';
import { pmCompositeMatcher } from '@shared/utils/procedure-map-composites';
import { Maybe } from '@/types/globals';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';

/**
 * @deprecated
 * This service is deprecated and will be left only for v0 in the future.
 * Some methods are still have to be used from this service even in v1 for now,
 * but please try not to add new methods here.
 * Please use RxMaterialRulesService, RxIndicationRulesService, and RxShadeSystemRulesService when possible.
 */
@Injectable({ providedIn: 'root' })
export class RxRulesService {
	private toothEditorRulesAbutmentTypesTranslated: AbutmentType[] = this.rxGeneralRules.ToothEditorRules.Abutment.AbutmentTypes;

	abutmentTypes$ = new BehaviorSubject<AbutmentType[]>(this.rxGeneralRules.ToothEditorRules.Abutment.AbutmentTypes);

	constructor(
		@Inject(PROCEDURE_TOOTH_EDITOR_PARSER) private readonly procedureToothEditorParser: ProcedureToothEditorParser,
		@Inject(RX_GENERAL_RULES) private readonly rxGeneralRules: RxGeneralRules,
		@Inject(CASETYPE_RULES_PARSER) private readonly caseTypeRulesParser: CaseTypeRulesParser,
		@Inject(PROCEDURE_BRIDGE_AVAILABILITY_RULES)
		private readonly procedureBridgeAvailabilityRules: ReadonlyArray<ProcedureMapCompositePattern>,
		@Inject(DENTURE_DETAILS_RULES) private readonly dentureDetailsRules: RxDentureDetailsRules,
		@Inject(RESTORATION_CONFIGURATION) private readonly restorationConfiguration: RestorationConfiguration,
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery,
		private readonly translateService: TranslateService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly toothEditorQuery: ToothEditorQuery,
		private readonly rxIndicationRulesService: RxIndicationRulesService,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly rxMaterialRulesService: RxMaterialRulesService
	) {}

	translateSelectOptions() {
		this.rxMaterialRulesService.translateCrownDesignSelectOptions();
		this.rxShadeSystemRulesService.translateShadeSystemOptions();
		this.toothEditorRulesAbutmentTypesTranslated = this.rxGeneralRules.ToothEditorRules.Abutment.AbutmentTypes.map(abutmentType =>
			this.getTranslatedObject<AbutmentType>(abutmentType, 'AbutmentTypes')
		);

		this.abutmentTypes$.next(this.toothEditorRulesAbutmentTypesTranslated);
	}

	getCaseTypeFlowUnitTypeNamesForBridge(isOuterTooth: boolean): IdName[] {
		return this.unitTypesInBridgeByToothPosition(isOuterTooth);
	}

	getCaseTypeFlowUnitTypeNamesForGlidewellBridge(isOuterTooth: boolean): IdName[] {
		return this.glidewellCaseUnitTypesInBridgeByToothPosition(isOuterTooth);
	}

	getProcedureFlowUnitTypeNames(tooth: Tooth): Observable<IdName[]> {
		return combineLatest([this.orderQuery.procedureMap$, this.shellQuery.rxConfiguration$]).pipe(
			take(1),
			map(([procedureMap, rxConfiguration]) =>
				this.rxIndicationRulesService.getProcedureFlowUnitTypeNames(
					tooth,
					procedureMap,
					rxConfiguration ?? undefined,
					this.hostPlatformService.isIteroModeling,
					!this.toothEditorQuery.isToothEditorOpen && this.isBridgeAvailableFor(procedureMap),
					id => this.rxIndicationRulesService.getUnitTypeTranslatedName(id)
				)
			)
		);
	}

	getCaseTypeFlowUnitTypeNames(tooth: Tooth): Observable<IdName[]> {
		return combineLatest([this.orderQuery.caseType$, this.shellQuery.rx$]).pipe(
			take(1),
			map(([caseTypeItem, existingRx]: [IdName, RxModel]) => {
				let unitTypeIds = this.caseTypeRulesParser.getUnitTypeIdsByCaseTypeIdAndToothId(
					caseTypeItem?.Id ?? existingRx?.Order?.CaseTypeId ?? 0,
					tooth.ToothID ?? 0,
					tooth.UnitTypeID as UnitTypes
				);

				if (!this.hostPlatformService.isIteroModeling) {
					unitTypeIds = this.rxIndicationRulesService.removeModelingSpecificUnitTypes(unitTypeIds);
				}

				return this.getFilteredAndCustomSortedUnitTypeMenuNames({
					unitTypeIds
				});
			})
		);
	}

	getMaterialIds(caseTypeId: number, toothId: number, currentUnitTypeOnTooth: UnitTypes): number[] {
		return this.caseTypeRulesParser.getMaterialIds(caseTypeId, toothId, currentUnitTypeOnTooth);
	}

	getMarginDesignOptions(): IdName[] {
		return this.rxMaterialRulesService.getMarginDesignOptions();
	}

	getAbutmentOptionsMapping(): IdName[] {
		return this.rxGeneralRules.IdNameMapping.Abutment;
	}

	getPreparationDesignOptionsMapping(): IdName[] {
		return this.rxMaterialRulesService.getPreparationDesignOptionsMapping();
	}

	isBridgeAvailableFor(pmToCheck: Maybe<ProcedureMapCheckedValue>): boolean {
		return this.procedureBridgeAvailabilityRules.some(rule => pmCompositeMatcher(rule, pmToCheck));
	}

	checkDentureDetailsRule(teeth: Tooth[], orderType: TypeEnum): boolean {
		const rule = this.dentureDetailsRules.ToothRules.find(r => r.OrderType === orderType)?.TeethTypes;

		return rule ? teeth.some(t => t.UnitTypeID && rule.includes(t.UnitTypeID)) : false;
	}

	getTiBase(abutmentTypeId: number): boolean {
		return this.restorationConfiguration.AbutmentTypeMappings.find(x => x.TargetId === abutmentTypeId)?.VisibleIsTiBase ?? false;
	}

	getUnitTypeConfiguration(rx: Maybe<RxModel>): TeethUnitType[] | null {
		if (this.shellQuery?.isProcedureFlow) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			return this.rxIndicationRulesService.getProcedureFlowUnitTypeConfiguration(rx);
		}

		return this.caseTypeRulesParser.getTeethUnitTypeRule(rx?.Order?.CaseTypeId ?? 0);
	}

	private getFilteredAndCustomSortedUnitTypeMenuNames({
		unitTypeIds,
		shouldSortByDefaultOrder = true
	}: {
		unitTypeIds: number[];
		shouldSortByDefaultOrder?: boolean;
	}): IdName[] {
		return this.rxIndicationRulesService.getFilteredAndCustomSortedUnitTypeMenuNames({
			unitTypeIds,
			shouldSortByDefaultOrder,
			getUnitTypeTranslatedNameOverride: id => this.rxIndicationRulesService.getUnitTypeTranslatedName(id)
		});
	}

	private glidewellCaseUnitTypesInBridgeByToothPosition(isOuterTooth: boolean): IdName[] {
		const innerBridgeTypes = [UnitTypesInBridge.Abutment, UnitTypesInBridge.Pontic, UnitTypesInBridge.Skipped];
		const outerBridgeTypes = [UnitTypesInBridge.Abutment, UnitTypesInBridge.Pontic];
		const bridgeTypesToShow = isOuterTooth ? outerBridgeTypes : innerBridgeTypes;

		return bridgeTypesToShow.map(id => ({
			Id: id,
			Name: this.rxIndicationRulesService.getBridgeUnitTypeTranslatedName(id)
		}));
	}

	private unitTypesInBridgeByToothPosition(isOuterTooth: boolean): IdName[] {
		const innerBridgeTypes = [
			UnitTypesInBridge.Abutment,
			UnitTypesInBridge.ImplantAbutment,
			UnitTypesInBridge.ScanBody,
			UnitTypesInBridge.Pontic,
			UnitTypesInBridge.Skipped
		];

		const outerBridgeTypes = [
			UnitTypesInBridge.Abutment,
			UnitTypesInBridge.ImplantAbutment,
			UnitTypesInBridge.ScanBody,
			UnitTypesInBridge.Pontic,
			UnitTypesInBridge.MarylandAbutment
		];
		const bridgeTypesToShow = isOuterTooth ? outerBridgeTypes : innerBridgeTypes;

		return bridgeTypesToShow.map(id => ({
			Id: id,
			Name: this.rxIndicationRulesService.getBridgeUnitTypeTranslatedName(id)
		}));
	}

	private getTranslatedObject<T extends { Name: string }>(object: T, sectionName: string): T {
		return {
			...object,
			Name: this.translateService.instant(`${sectionName}.${object.Name}`)
		};
	}
}
